import { useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { usePopoverBehavior } from "@/hooks/usePopoverBehavior";
import { formatDate } from "@/lib/timeHelpers";

// ─── Constants ────────────────────────────────────────────────
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// ─── Helpers ──────────────────────────────────────────────────
function isSameDay(a, b) {
  return (
    a instanceof Date && b instanceof Date &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

// ─── Main export ──────────────────────────────────────────────
/**
 * CalendarPopup — date selector that positions a calendar popover below the trigger.
 *
 * Props (single mode — current):
 *   value:    Date | null
 *   onChange: (Date) => void
 *
 * Props (range mode — future, reserved):
 *   value:    { from: Date, to: Date | null } | null
 *   onChange: ({ from: Date, to: Date | null }) => void
 *
 * mode: "single" | "range"   (default "single")
 */
export default function CalendarPopup({
  value,
  onChange,
  mode = "single",
  placeholder = "Pick a date",
  id,
  error = false,
  disabled = false,
}) {
  const { open, setOpen, openPopover: openPopoverBase, popoverStyle, triggerRef, popoverRef } =
    usePopoverBehavior({ minWidth: 256 });

  const selectedDate = mode === "single" ? value : value?.from ?? null;
  const [viewYear, setViewYear] = useState(() => (selectedDate ?? new Date()).getFullYear());
  const [viewMonth, setViewMonth] = useState(() => (selectedDate ?? new Date()).getMonth());

  function openPopover() {
    if (disabled) return;
    openPopoverBase();
    // Sync calendar view to currently selected date
    const ref = selectedDate ?? new Date();
    setViewYear(ref.getFullYear());
    setViewMonth(ref.getMonth());
  }

  function handleDaySelect(date) {
    if (mode === "single") {
      onChange(date);
      setOpen(false);
      return;
    }
    // Range mode: two-click pattern — wired in when multi-day is needed
    // First click sets `from`, second sets `to`
    const range = value ?? {};
    if (!range.from || (range.from && range.to)) {
      onChange({ from: date, to: null });
    } else {
      const [start, end] = date < range.from
        ? [date, range.from]
        : [range.from, date];
      onChange({ from: start, to: end });
      setOpen(false);
    }
  }

  const displayValue = mode === "single"
    ? formatDate(value)
    : value?.from
      ? value.to
        ? `${formatDate(value.from)} – ${formatDate(value.to)}`
        : formatDate(value.from)
      : "";

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        onClick={openPopover}
        disabled={disabled}
        className={cn(
          "flex h-10 w-full items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm text-left transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50",
          error ? "border-destructive" : "border-input",
          !displayValue && "text-muted-foreground",
        )}
      >
        <CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="flex-1 truncate">{displayValue || placeholder}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground opacity-60" />
      </button>

      {open && createPortal(
        <div
          ref={popoverRef}
          style={popoverStyle}
          className="rounded-xl border border-border bg-popover shadow-lg p-3 select-none"
        >
          <CalendarGrid
            viewYear={viewYear}
            viewMonth={viewMonth}
            onViewChange={(y, m) => { setViewYear(y); setViewMonth(m); }}
            onDaySelect={handleDaySelect}
            selectedDate={mode === "single" ? value : value?.from}
            rangeValue={mode === "range" ? value : null}
            mode={mode}
          />
        </div>,
        document.body,
      )}
    </>
  );
}

// ─── Calendar grid (exported for reuse in DateTimePopup) ─────
export function CalendarGrid({
  viewYear, viewMonth, onViewChange,
  onDaySelect, selectedDate, rangeValue, mode,
}) {
  const [hovered, setHovered] = useState(null);
  const today = new Date();

  const firstDow = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [
    ...Array(firstDow).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  function prevMonth() {
    viewMonth === 0 ? onViewChange(viewYear - 1, 11) : onViewChange(viewYear, viewMonth - 1);
  }
  function nextMonth() {
    viewMonth === 11 ? onViewChange(viewYear + 1, 0) : onViewChange(viewYear, viewMonth + 1);
  }

  function dayDate(day) {
    return new Date(viewYear, viewMonth, day);
  }

  function isSelected(day) {
    if (!day) return false;
    return isSameDay(selectedDate, dayDate(day));
  }

  function isRangeStart(day) {
    return mode === "range" && day && isSameDay(rangeValue?.from, dayDate(day));
  }

  function isRangeEnd(day) {
    return mode === "range" && day && isSameDay(rangeValue?.to, dayDate(day));
  }

  function isInRange(day) {
    if (mode !== "range" || !day || !rangeValue?.from) return false;
    const d = dayDate(day);
    const end = rangeValue.to ?? hovered;
    if (!end) return false;
    const [lo, hi] = rangeValue.from <= end ? [rangeValue.from, end] : [end, rangeValue.from];
    return d > lo && d < hi;
  }

  function isToday_(day) {
    return day && isSameDay(dayDate(day), today);
  }

  return (
    <div className="w-56">
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={prevMonth}
          className="p-1.5 rounded-md hover:bg-muted transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-semibold">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </span>
        <button
          type="button"
          onClick={nextMonth}
          className="p-1.5 rounded-md hover:bg-muted transition-colors"
          aria-label="Next month"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Weekday labels */}
      <div className="grid grid-cols-7 mb-1">
        {DAY_LABELS.map((l) => (
          <div key={l} className="text-center text-[10px] font-medium text-muted-foreground py-0.5">
            {l}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7">
        {cells.map((day, i) => {
          const sel  = isSelected(day);
          const rSt  = isRangeStart(day);
          const rEn  = isRangeEnd(day);
          const inR  = isInRange(day);
          const tod  = isToday_(day);

          return (
            <div
              key={i}
              className={cn(
                inR && "bg-accent",
                rSt && "rounded-l-full bg-accent",
                rEn && "rounded-r-full bg-accent",
              )}
            >
              <button
                type="button"
                onClick={() => day && onDaySelect(dayDate(day))}
                onMouseEnter={() => day && setHovered(dayDate(day))}
                onMouseLeave={() => setHovered(null)}
                disabled={!day}
                className={cn(
                  "h-8 w-full rounded-full text-sm transition-colors",
                  !day && "invisible pointer-events-none",
                  day && !sel && "hover:bg-muted",
                  sel && "bg-primary text-primary-foreground hover:bg-primary/90",
                  tod && !sel && "font-bold text-primary",
                )}
              >
                {day}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
